import { useRef, useState } from "react";
import { useIntl } from "react-intl";
import { userErrorMessage } from "@/lib/user-error";

type SectionResult = { id: string; hiddenSections?: readonly string[] | null };
type Selection = {
  result: SectionResult;
  hidden: readonly string[];
  saving: boolean;
  error: string | null;
};

/** Keep optimistic changes scoped to the opened snapshot; roll back to the last saved choice. */
export function useResultSections(
  result: SectionResult | null,
  update: (id: string, hidden: string[]) => Promise<void>,
) {
  const intl = useIntl();
  const [selection, setSelection] = useState<Selection | null>(null);
  const inFlight = useRef(new Set<SectionResult>());
  const current = selection?.result === result ? selection : null;
  const hiddenSections = current?.hidden ?? result?.hiddenSections ?? [];

  async function change(next: string[]) {
    if (!result || inFlight.current.has(result)) return;
    inFlight.current.add(result);
    const previous = hiddenSections;
    setSelection({ result, hidden: next, saving: true, error: null });
    try {
      await update(result.id, next);
      setSelection((state) => state?.result === result ? { ...state, saving: false } : state);
    } catch (error) {
      setSelection((state) => state?.result === result
        ? { result, hidden: previous, saving: false, error: userErrorMessage(intl, error) }
        : state);
    } finally {
      inFlight.current.delete(result);
    }
  }

  return { hiddenSections, saving: current?.saving ?? false, error: current?.error ?? null, change };
}
